/* eslint-disable react/prop-types */
import React, { useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import Loading from 'BridgReusables/Loading';
import * as styles from './FourZeroFourPage.scss';

const ERROR_PAGE_DELAY_MS = 1000;

/**
 * 404 Route Page
 * @function FourZeroFourPage
 * @param {Object} props
 * @return {ReactElement}
 */
const FourZeroFourPage = () => {
  const { sid: accountSid } = useSelector((state) => state.app.account);
  const { sid: brandSid, category: brandCategory } = useSelector((state) => state.app.brand);

  const {
    enableTsCoreInsights,
    requiresThoughtspot,
    enableSegmentsRoute,
    enableAudiencesRoute: isAudiencesRouteAvailable,
    enableReportsRoute,
    showBridgExplorer,
  } = useFlags();

  const [content, setContent] = React.useState(<Loading />);

  const renderSuggestedRoutes = useCallback(() => {
    if (accountSid && brandSid) {
      const isCpgBrand = brandCategory === 'CPG';
      const isInsightsRouteAvailable = enableTsCoreInsights && requiresThoughtspot;
      const isSegmentsRouteAvailable = enableSegmentsRoute && !isCpgBrand;
      const isExplorerRouteAvailable = showBridgExplorer && requiresThoughtspot;
      const isReportsRouteAvailable = enableReportsRoute && requiresThoughtspot;

      return (
        <div>
          <div className={styles.header}>Here&apos;s some pages that do exist:</div>
          <div className={styles.suggestedRoutes}>
            {isInsightsRouteAvailable && <Link className={styles.link} to={`/${accountSid}/${brandSid}/core-insights`}>Insights</Link>}
            {isSegmentsRouteAvailable && <Link className={styles.link} to={`/${accountSid}/${brandSid}/segments`}>Segments</Link>}
            {isAudiencesRouteAvailable && <Link className={styles.link} to={`/${accountSid}/${brandSid}/audiences`}>Audiences</Link>}
            {isExplorerRouteAvailable && <Link className={styles.link} to={`/${accountSid}/${brandSid}/explorer`}>Explorer</Link>}
            {isReportsRouteAvailable && <Link className={styles.link} to={`/${accountSid}/${brandSid}/reports`}>Reports</Link>}
          </div>
        </div>
      );
    } return (
      <div>
        <div className={styles.suggestedRoutes}>
          <Link className={styles.link} to="/">Go back home</Link>
        </div>
      </div>
    );
  }, [
    accountSid,
    brandCategory,
    brandSid,
    enableReportsRoute,
    enableSegmentsRoute,
    enableTsCoreInsights,
    isAudiencesRouteAvailable,
    requiresThoughtspot,
    showBridgExplorer,
  ]);

  // Set the error page content after a delay to prevent a flicker if LaunchDarkly
  // flags aren't settled yet. Necessary because some flags depend on the user
  // context, which is not initially available.
  useEffect(() => {
    const timer = setTimeout(() => {
      setContent(
        <div className={styles.fourZeroFourPage}>
          <div className={styles.messageContainer}>
            <span className={styles.title}>Error 404</span>
            <span className={styles.subtitle}>That page doesn&apos;t exist.</span>
            { renderSuggestedRoutes() }
          </div>
        </div>
      );
    }, ERROR_PAGE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [renderSuggestedRoutes]);

  return content;
};

export default FourZeroFourPage;
